import { useEffect, useId, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Maximize2 } from 'lucide-react'
import { logger } from '@/core/logger'
import { colorToHex } from '@/shared/theme-lab/color-math'
import { Modal } from './modal'

export interface MermaidDiagramProps {
  /**
   * Mermaid source — any flavour (stateDiagram-v2, flowchart, erDiagram, etc.).
   *
   * Security boundary: this prop must only receive **dev-authored constants**
   * (e.g. `BOOKING_STATE_DIAGRAM`, `PRICING_PRECEDENCE_DIAGRAM`,
   * `CANCELLATION_DECISION_DIAGRAM`) — never strings sourced from Firestore,
   * URL params, or any other user input. Mermaid has a long history of XSS /
   * prototype-pollution CVEs; treating its input as trusted is only safe
   * because we control every byte of it. If a feature ever needs to render
   * user-supplied diagram source, route it through a Cloud Function that
   * pre-renders to SVG and serve the SVG string instead.
   */
  source: string
  /** Short label rendered above the diagram + used as the expand-modal title. */
  caption?: string
  /** Extra className applied to the wrapper. */
  className?: string
  /**
   * When true (default) the figure shows a small ↗ button in its corner that
   * opens a full-width modal with the same diagram re-rendered at larger
   * size. Set to false for decorative / already-large diagrams.
   */
  expandable?: boolean
}

// Mermaid is heavy (~350 KB gzipped). Lazy-load once per session; subsequent
// diagrams reuse the cached module.
let mermaidModulePromise: Promise<typeof import('mermaid')['default']> | null = null

// Mermaid's color parser doesn't understand `oklch(...)` — and our design
// tokens are oklch since the OKLCH refactor. Resolve the var via a real
// probe element, then convert whatever it returns (rgb / oklch / hsl) into
// `#rrggbb` so mermaid never sees a syntax it can't parse.
//
// Probe alone isn't enough: Chromium ≥ 111 may return `oklch(...)` verbatim
// from `getComputedStyle` rather than rgb(), which is exactly what was
// firing CLUBMANAGER-PWA-15.
function resolveColorToken(varName: string, fallback: string): string {
  if (typeof document === 'undefined') return fallback
  const probe = document.createElement('div')
  probe.style.color = `var(${varName}, ${fallback})`
  probe.style.display = 'none'
  document.body.appendChild(probe)
  const resolved = getComputedStyle(probe).color
  document.body.removeChild(probe)
  return colorToHex(resolved || fallback, fallback)
}

function loadMermaid(): Promise<typeof import('mermaid')['default']> {
  if (!mermaidModulePromise) {
    mermaidModulePromise = import('mermaid').then((m) => {
      // Re-read Night Ops design tokens at runtime so the diagram matches
      // whatever dark-theme-first palette the app is using. Fall back to
      // reasonable hardcoded values if resolution fails (e.g. SSR / tests).
      const cs = typeof window !== 'undefined' ? getComputedStyle(document.documentElement) : null
      const pickFont = (name: string, fallback: string) =>
        cs?.getPropertyValue(name)?.trim() || fallback
      m.default.initialize({
        startOnLoad: false,
        theme: 'base',
        themeVariables: {
          background: resolveColorToken('--panel', '#0d1117'),
          primaryColor: resolveColorToken('--panel-2', '#1a222e'),
          primaryTextColor: resolveColorToken('--fg', '#e6edf3'),
          primaryBorderColor: resolveColorToken('--accent', '#5db3ff'),
          lineColor: resolveColorToken('--fg-muted', '#8899a6'),
          secondaryColor: resolveColorToken('--panel', '#0d1117'),
          tertiaryColor: resolveColorToken('--panel', '#0d1117'),
          fontFamily: pickFont('--font-sans', 'Inter, sans-serif'),
          fontSize: '14px',
        },
        securityLevel: 'strict',
      })
      return m.default
    })
  }
  return mermaidModulePromise
}

/**
 * Render a Mermaid diagram. The module is dynamic-imported the first time
 * any diagram mounts so pages that never open the Help sheet never pay the
 * bundle cost.
 *
 * See `docs/ui-ux-patterns.md` for when to use a diagram vs prose. Rule of
 * thumb: if the thing has more than 3 branches or a non-linear flow, a
 * diagram is easier to audit than a paragraph.
 */
export function MermaidDiagram({
  source,
  caption,
  className,
  expandable = true,
}: MermaidDiagramProps) {
  const [expandedOpen, setExpandedOpen] = useState(false)
  return (
    <>
      <MermaidDiagramSvg source={source} caption={caption} className={className}>
        {expandable && (
          <button
            type="button"
            className="mermaid-expand-btn"
            onClick={() => setExpandedOpen(true)}
            aria-label={caption ? `Ampliar: ${caption}` : 'Ampliar diagrama'}
          >
            <Maximize2 size={12} />
          </button>
        )}
      </MermaidDiagramSvg>
      {expandedOpen &&
        // Portal to document.body so the modal escapes any `will-change` /
        // `transform` ancestor (e.g. the help SidePanel's drawer) that would
        // otherwise become the modal's containing block. Without this the
        // modal centers on the drawer instead of the whole viewport.
        createPortal(
          <Modal
            title={caption ?? 'Diagrama'}
            size="xl"
            onClose={() => setExpandedOpen(false)}
          >
            <div className="mermaid-expanded">
              <MermaidDiagramSvg source={source} largeScale />
            </div>
          </Modal>,
          document.body,
        )}
    </>
  )
}

/**
 * The actual Mermaid render — extracted so the expand-modal can show a
 * second instance of the same source (at larger size) without sharing
 * render IDs with the thumbnail.
 */
function MermaidDiagramSvg({
  source,
  caption,
  className,
  children,
  largeScale = false,
}: {
  source: string
  caption?: string | undefined
  className?: string | undefined
  children?: React.ReactNode
  largeScale?: boolean
}) {
  const hostRef = useRef<HTMLDivElement>(null)
  const reactId = useId()
  const renderId = `mermaid-${reactId.replace(/[^a-zA-Z0-9_-]/g, '')}${
    largeScale ? '-xl' : ''
  }`
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setError(null)
    loadMermaid()
      .then(async (mermaid) => {
        if (cancelled || !hostRef.current) return
        try {
          const { svg } = await mermaid.render(renderId, source)
          if (cancelled || !hostRef.current) return
          hostRef.current.innerHTML = svg
        } catch (err) {
          logger.w('MermaidDiagram render failed', err)
          setError('No pudimos renderizar el diagrama.')
        }
      })
      .catch((err) => {
        logger.w('MermaidDiagram module load failed', err)
        setError('No pudimos cargar el módulo de diagramas.')
      })
    return () => {
      cancelled = true
    }
  }, [source, renderId])

  return (
    <figure
      className={`mermaid-figure${largeScale ? ' mermaid-figure--xl' : ''}${className ? ' ' + className : ''}`}
    >
      {caption && <figcaption className="mermaid-caption">{caption}</figcaption>}
      {error ? (
        <div role="alert" className="mermaid-error">
          {error}
        </div>
      ) : (
        <div
          ref={hostRef}
          className="mermaid-host"
          aria-label={caption ?? 'Diagrama'}
          role="img"
        />
      )}
      {children}
    </figure>
  )
}
